
import { fetchCompleteStop, fetchSetJobCancel, fetchSetJobAsFutile } from '@/helpers/fetchers';
import Button from '@/components/UI/Button.vue';
import Icon from '@/components/UI/Icon.vue';
import { Component, Prop, Vue } from 'vue-property-decorator';

import { StopCompleteParams } from '@/models/main';
import { JobDetailedData, JobDetailedCompleteData, JobCancelParams, JobFutileParams } from '@/models/jobDetailed';

// Constants for action types
const ACTION_TYPES = {
  COMPLETE: 'complete',
  FUTILE: 'futile',
  CANCEL: 'cancel',
};

// Interface for dialog options
interface DialogOption {
  id: string;
  title: string;
  fields: string[];
  action: () => Promise<void>;
}

@Component({
  components: {
    Button,
    Icon,
  },
})
export default class StopCompleteLastStopDialog extends Vue {
  @Prop(Object) public completeJobData: JobDetailedCompleteData;
  @Prop(Object) public job!: JobDetailedData;

  public loading = false;
  public selectedOption: string | null = null;
  public formData = {
    driver: '',
    name: '',
    reason: '',
  };

  get isJobCancelled(): boolean {
    return this.job.status.toUpperCase() === 'CANCELLED';
  }

  // Computed property to determine available options
  get availableOptions(): DialogOption[] {
    const options: DialogOption[] = [];
    // Complete job option (only if allocated)
    if (this.job.allocation_state.toUpperCase() !== 'DEALLOCATED') {
      options.push({
        id: ACTION_TYPES.COMPLETE,
        title: 'The driver completed all stops',
        fields: this.job.require_pod ? ['driver'] : [],
        action: this.setJobAsComplete,
      });
      // Close job as futile option
      options.push({
        id: ACTION_TYPES.FUTILE,
        title: 'Close the job as futile',
        fields: ['name', 'reason'],
        action: this.setJobAsFutileClicked,
      });
    } else {
      // Cancel job option (only if not allocated)
      options.push({
        id: ACTION_TYPES.CANCEL,
        title: 'Cancel the job',
        fields: ['name', 'reason'],
        action: this.setJobAsCancelClicked,
      });
    }
    return options;
  }

  public onOptionSelect(optionId: string): void {
    this.selectedOption = optionId;
    this.resetFormData();
    this.$nextTick(() => {
      const form = this.$refs[`form-${optionId}`] as HTMLFormElement[];
      if (form && form[0]) {
        form[0].resetValidation();
      }
    });
  }

  private resetFormData(): void {
    Object.keys(this.formData).forEach(key => {
      this.formData[key] = '';
    });
  }

  // SUBMIT BUTTON
  public submit(): void {
    if (!this.selectedOption) {
      return;
    }
    const selectedOptionObj = this.availableOptions.find(option => option.id === this.selectedOption);
    if (!selectedOptionObj) {
      return;
    }
    // If no fields to fill, execute action immediately
    if (selectedOptionObj.fields.length === 0) {
      selectedOptionObj.action();
      return;
    }
    // Form validation, if there are fields
    const form = this.$refs[`form-${this.selectedOption}`] as HTMLFormElement[];
    if (form && form[0] && form[0].validate()) {
      selectedOptionObj.action();
    }
  }

  // ACTIONS:
  public async setJobAsComplete(): Promise<void> {
    this.loading = true;
    let params: StopCompleteParams = {
      driver_id: this.job.driver_code || 0,
      job_id: this.job.rockhopper_id,
      job_date: this.job.rockhopper_date,
      stop_number: this.completeJobData.stop,
    };
    if (this.job.require_pod) {
      params = { ...params, pod_name: this.formData.driver };
    }
    return fetchCompleteStop('departed', params)
      .then(() => void this.$emit('fetchData'))
      .catch(() => void {})
      .finally(() => (this.loading = false));
  }

  public setJobAsFutileClicked(): Promise<void> {
    this.loading = true;
    const params: JobFutileParams = {
      job_id: this.job.rockhopper_id,
      job_date: this.job.rockhopper_date,
      driver: -1,
      reason: this.formData.reason,
    };
    return fetchSetJobAsFutile(params)
      .then(() => void this.$emit('fetchData'))
      .catch(() => void {})
      .finally(() => (this.loading = false));
  }

  public setJobAsCancelClicked(): Promise<void> {
    this.loading = true;
    const params: JobCancelParams = {
      job_id: this.job.rockhopper_id,
      job_date: this.job.rockhopper_date,
      actor: this.formData.name,
      reason: this.formData.reason,
    };
    return fetchSetJobCancel(params)
      .then(() => void this.$emit('fetchData'))
      .catch(() => void {})
      .finally(() => (this.loading = false));
  }
}
